/**
 * /app/$tenantSlug/settings/modules — Settings → Organization → Modules
 * (docs/56 U1 — the breadth-vs-simplicity lever).
 *
 * The tenant's own module ON/OFF switches. Subtractive within the plan:
 * switching a module OFF hides it everywhere (sidebar, command palette,
 * feature gates) for EVERY member; switching it back on restores it
 * instantly — module data is never touched. The backend applies the
 * off-list LAST in ResolveFeatures, so this surface can never grant
 * anything the plan/add-ons don't include (down-only by construction).
 *
 * Write is company_admin-only (backend RBAC is the boundary; hr_manager
 * sees read-only switches). POSH gets a confirm dialog before switching
 * off — it's a statutory compliance module, not a convenience feature.
 * After a successful save we refresh the auth context so /me.features —
 * and with it the whole nav — updates without a reload.
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Blocks } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth, usePoshOnly } from "@/lib/auth";
import { settingsApi, toApiError } from "@/lib/api";
import type { ModuleState } from "@/lib/api/settings";
import { MODULE_META } from "@/lib/module-meta";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { NotInPoshOnlyTiles } from "@/components/billing/not-in-posh-only";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/modules",
)({
  // A9 hover-prefetch. One HR-role-gated GET; the settings nav only
  // surfaces this link to HR admins, so in practice the loader fires
  // for callers the backend accepts. Errors are caught — the page's own
  // useQuery error state stays the authority.
  loader: ({ context }) =>
    context.queryClient
      .ensureQueryData({
        queryKey: MODULES_QUERY_KEY,
        queryFn: ({ signal }) => settingsApi.getModules(signal),
      })
      .catch(() => undefined),
  component: SettingsModulesRoute,
});

const MODULES_QUERY_KEY = ["settings", "modules"] as const;

function SettingsModulesRoute() {
  const { user, refresh } = useAuth();
  const qc = useQueryClient();
  const isCompanyAdmin = user?.role === "company_admin";
  // POSH only (migration 162): below the switches, what the plan leaves out.
  const poshOnly = usePoshOnly();

  const modulesQ = useQuery({
    queryKey: MODULES_QUERY_KEY,
    queryFn: ({ signal }) => settingsApi.getModules(signal),
  });

  // POSH switch-off confirm state (statutory module — deliberate step).
  const [confirmOff, setConfirmOff] = useState<ModuleState | null>(null);

  const save = useMutation({
    mutationFn: (disabled: string[]) => settingsApi.putModules(disabled),
    onSuccess: async (fresh) => {
      qc.setQueryData(MODULES_QUERY_KEY, fresh);
      // Refresh /me so features — and with them the sidebar, palette and
      // every feature gate — update immediately, no reload.
      await refresh();
      toast.success("Modules updated.");
    },
    onError: (err) => {
      toast.error(toApiError(err).message || "Could not update modules.");
    },
  });

  const modules = modulesQ.data?.modules ?? [];

  function apply(feature: string, enabled: boolean) {
    const disabled = modules
      .filter((m) => (m.feature === feature ? !enabled : !m.enabled))
      .map((m) => m.feature);
    save.mutate(disabled);
  }

  function onToggle(m: ModuleState, next: boolean) {
    if (!next && m.feature === "posh") {
      setConfirmOff(m);
      return;
    }
    apply(m.feature, next);
  }

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold">Modules</h2>
            <p className="text-sm text-muted-foreground">
              Switch off what your team doesn&apos;t use — the sidebar stays
              lean, and nothing is deleted. Switch a module back on any time;
              its data is exactly where you left it.
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Blocks className="h-4 w-4 text-primary" />
                Your Plan&apos;s Modules
              </CardTitle>
              <CardDescription>
                {isCompanyAdmin
                  ? "Changes apply to everyone in the workspace immediately."
                  : "Only your company admin can switch modules on or off."}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {modulesQ.isLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : modulesQ.isError ? (
                <Alert variant="destructive">
                  <AlertTitle>Couldn&apos;t load modules</AlertTitle>
                  <AlertDescription>
                    {toApiError(modulesQ.error).message ||
                      "Please refresh and try again."}
                  </AlertDescription>
                </Alert>
              ) : (
                <ul className="divide-y divide-border">
                  {modules.map((m) => {
                    const meta = MODULE_META[m.feature] ?? {
                      label: m.feature,
                      blurb: "",
                    };
                    return (
                      <li
                        key={m.feature}
                        className="flex items-center justify-between gap-4 py-3"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground">
                            {meta.label}
                          </p>
                          {meta.blurb && (
                            <p className="text-xs text-muted-foreground">
                              {meta.blurb}
                            </p>
                          )}
                        </div>
                        <Switch
                          checked={m.enabled}
                          disabled={!isCompanyAdmin || save.isPending}
                          onCheckedChange={(next) => onToggle(m, next)}
                          aria-label={`${meta.label} ${m.enabled ? "on" : "off"}`}
                          data-testid={`module-switch-${m.feature}`}
                        />
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>

          {poshOnly && <NotInPoshOnlyTiles />}
        </div>
      </SettingsShell>

      {/* POSH switch-off confirm — statutory module, deliberate step. */}
      <AlertDialog
        open={confirmOff !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmOff(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Switch off POSH compliance?</AlertDialogTitle>
            <AlertDialogDescription>
              The POSH Act 2013 applies to every Indian workplace with 10 or
              more employees. Switching this module off hides training, the
              ICC and complaint intake for everyone — it does not pause any
              statutory obligation, and existing records are kept. You can
              switch it back on at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep It On</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmOff) apply(confirmOff.feature, false);
                setConfirmOff(null);
              }}
            >
              Switch Off
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
